import { z } from "zod";
import { ActorId, DistrictId, IsoUtc, PropertyId, Uuid, finiteNumber } from "./primitives";
import { WorldCopyNumber } from "./worlds";

export const ROOM_NAMES = { street: "street", interior: "interior" } as const;
export type RoomName = (typeof ROOM_NAMES)[keyof typeof ROOM_NAMES];

/**
 * What a browser asks a room ticket for (`POST /api/v1/room-tickets`). Town v6 (ADR-0042): the street is ONE room for
 * the whole town; a street target's optional `districtId` only says where a fresh join lands (that district's square).
 * Absent: the district the visitor was last in (the bootstrap's `startDistrictId` rule). It never selects a room.
 * ADR-0043 (named world copies): the API picks the copy (the placement rule, `chooseStreetCopy`: the visitor's last copy
 * while it is open and has room, else the fullest open copy below the cap, else a new copy); the browser never names
 * one. `avoidCopy` is the one retry after a ROOM_FULL race: the copy that just refused this visitor is skipped (it can
 * open at most one copy that would not otherwise be needed). Switching copies goes through the room (`world.switch`).
 */
export const RoomTarget = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("street"), districtId: DistrictId.optional(), avoidCopy: WorldCopyNumber.optional() }),
  z.object({ kind: z.literal("interior"), propertyId: PropertyId }),
]);
export type RoomTarget = z.infer<typeof RoomTarget>;

export const RoomTicketRequest = z.object({ target: RoomTarget });
export type RoomTicketRequest = z.infer<typeof RoomTicketRequest>;
export const RoomTicketResponse = z.object({
  ticket: z.string().min(32).max(128),
  roomName: z.enum(["street", "interior"]),
  /** Join options the client must pass verbatim (room selection only, never authority). */
  joinOptions: z.record(z.string(), z.string()),
  expiresAt: IsoUtc,
});
export type RoomTicketResponse = z.infer<typeof RoomTicketResponse>;

/**
 * The middle segment of the town's street room key. Town v6 (ADR-0042): one street room for the whole town,
 * `street:<worldVersion>:town:<instance>` (`street:6:town:1`); no district is ever named `town`.
 */
export const TOWN_STREET_SEGMENT = "town";

/**
 * Canonical room keys. One canonical interior per property (every world copy shares it); town v6 (ADR-0042): one street
 * room per copy of the whole town, `street:<v>:town:<copy>` (ADR-0043: copy 1 is Robin; world v5 had one per district,
 * `street:5:<districtId>:1`; a ticket for such a key is refused like any other key of an older world).
 */
export const streetRoomKey = (worldVersion: number, copy = 1) => `street:${worldVersion}:${TOWN_STREET_SEGMENT}:${copy}`;
export const interiorRoomKey = (propertyId: string) => `interior:${propertyId}`;

/**
 * True for a street room key of ANY world version: the town's (`street:6:town:1`) and the older per-district ones
 * (`street:5:riverside:1`, `street:4:central:1`); false for an interior or a malformed key. A saved `last_room_key` of
 * a street comes with a position in the shared world frame (the bootstrap reads the start district from it).
 */
export function isStreetRoomKey(roomKey: string): boolean {
  const m = /^street:\d{1,6}:([^:]+):\d{1,6}$/.exec(roomKey);
  return Boolean(m && DistrictId.safeParse(m[1]).success);
}

/**
 * Join options of a street ticket. Always server-issued (the API for a fresh join or "Go there", the rooms for
 * transitions) and passed back verbatim; the room reads them from the ticket, never from the client. Town v6 (ADR-0042):
 * the ticket's room key names the room (ADR-0043: the copy), so no option is required (an empty set is valid: Robin):
 *  - `copy` (ADR-0043): the copy number as a decimal string, repeated so the matchmaker routes the join to that copy's
 *    room (`filterBy(["copy"])`); the ticket's room key `street:<v>:town:<copy>` must agree, or the join is refused;
 *  - `arriveX` / `arriveZ` / `arriveFacing` (ADR-0043, a switch between copies): appear at a free spot near that point
 *    of the town (the visitor's position in the copy they left, read by THAT room); a point outside the town or with no
 *    free spot near it lands on its district's square;
 *  - `spawnDistrictId`: a fresh join lands on that district's square (the API sets it from the request's `districtId`
 *    or the district the visitor was last in); absent or unknown (ADR-0043 decision 2: a new visitor with nothing
 *    saved): Central's square until Central holds `ARRIVAL_FILL_FIRST` people, then the least crowded of the arrival
 *    squares (`ARRIVAL_DISTRICT_IDS`);
 *  - `returnFromPropertyId`: leaving that property's interior: appear at its entrance, whatever district it is in
 *    (it wins over `spawnDistrictId`).
 * World v5's `districtId` (room membership) and `arriveViaGateId` (gates) are gone; the room ignores unknown options.
 */
export const STREET_JOIN_OPTIONS = {
  spawnDistrictId: "spawnDistrictId",
  returnFromPropertyId: "returnFromPropertyId",
  copy: "copy",
  arriveX: "arriveX",
  arriveZ: "arriveZ",
  arriveFacing: "arriveFacing",
} as const;

/**
 * ADR-0043: an interior ticket's `fromCopy` (server-issued, a decimal string): the world copy the visitor entered from.
 * Leaving the building returns them to it while it is open and has room (otherwise the placement rule picks one), their
 * presence names it while they are inside (the "which world" lookup), and the HUD shows it. Every copy shares the
 * building's ONE interior room, so the option never selects a room.
 */
export const INTERIOR_JOIN_OPTIONS = { propertyId: "propertyId", fromCopy: "fromCopy" } as const;

/**
 * ADR-0043 decision 2 (spread arrivals): the squares a new visitor (nothing saved, no district asked for) may land on,
 * in tie-break order, the first (Central) filled first (`ARRIVAL_FILL_FIRST`); then the street room picks the one whose
 * district has the fewest people right now. The Sports Park (ADR-0044) is left out on purpose: it has no shops, so a
 * first visit there would not show the advertising town (the choice is recorded in ADR-0043's slice 1 notes).
 */
export const ARRIVAL_DISTRICT_IDS = ["central", "riverside", "hillcrest"] as const;

/**
 * Founder decision 2026-10-25 ("Central until busy"): new visitors land in Central (the first arrival district) until
 * this many people are in Central, so the first visitors meet each other; only then do arrivals spread to the least
 * crowded arrival square.
 */
export const ARRIVAL_FILL_FIRST = 20;
export const StreetJoinOptions = z.object({
  spawnDistrictId: DistrictId.optional(),
  returnFromPropertyId: PropertyId.optional(),
  copy: z.string().regex(/^\d{1,2}$/).optional(),
  arriveX: z.string().max(32).optional(),
  arriveZ: z.string().max(32).optional(),
  arriveFacing: z.string().max(32).optional(),
});
export type StreetJoinOptions = z.infer<typeof StreetJoinOptions>;

/**
 * What lies behind a walk-through door of a room (the realtime room's door candidates): a property to enter (street)
 * or the way out (interior). Town v6 (ADR-0042): there are no district gates any more; the districts are joined by road.
 */
export type WalkTarget = { kind: "enter"; propertyId: string } | { kind: "exit" };

// ---------- client -> server messages ----------
export const CLIENT_MESSAGES = {
  inputMove: "input.move",
  chatSend: "chat.send",
  interaction: "interaction",
  presenceHint: "presence.hint",
  /** Driving input while in a car (InputDrive, vehicles.ts). */
  inputDrive: "input.drive",
  /** Get into / out of a town car (VehicleAction). */
  vehicleAction: "vehicle.action",
  /** Attractions (ADR-0016): an intent for the venue's game (VenueActionMessage, venues/common.ts). */
  venueAction: "venue.action",
  /**
   * ADR-0043 (nearby-only syncing, protocol 4): where this tab's Overview camera looks, so the street room also sends
   * the actors and cars around it (ViewFocus). Street rooms only; interiors ignore it.
   */
  viewFocus: "view.focus",
  /**
   * ADR-0043 (named world copies): move to another copy, from the Worlds list or "Join them in Finch" (WorldSwitch).
   * Answered with `room.transition_ready` or `world.switch_refused`. Street and interior rooms.
   */
  worldSwitch: "world.switch",
  /**
   * ADR-0053: this visitor is writing a chat line (a key pressed in the chat box, or the person card's Say hi), so the AI
   * resident they are talking with waits for it instead of saying goodbye (ChatTyping). Carries nothing, is never
   * broadcast or stored, and only delays a goodbye. Street rooms only; interiors ignore it.
   */
  chatTyping: "chat.typing",
} as const;

/**
 * Nearby-only syncing in the street room (ADR-0043 decision 1, protocol 4). Each tab receives only the actors (people
 * and AI residents) and cars near its focus points; everything the server decides (movement, traffic, seats, doors,
 * residents) still uses the whole room. Membership has hysteresis: an entity enters a tab's view inside a focus'
 * inner radius and leaves it only beyond the outer radius (inner + `hysteresisMetres`), checked `updateHz` times a second.
 *  - The tab's own avatar (or the car it drives: the driver's position is the car's) is always a focus, with
 *    `avatarRadiusMetres`: just inside the walk camera's fog (it starts at 220 m; a person there is ~7 px tall at
 *    1366 x 768), so whatever the walk camera shows crisply is synced.
 *  - In Overview the tab also sends its camera's ground focus (`view.focus`, at most `focusSendHz` a second); the
 *    server clamps the point to the town's bounds and the radius to [overviewRadiusMinMetres, overviewRadiusMaxMetres]
 *    (400 m: about 1.5 x the 280 m zoom limit, where a person is ~5 px tall).
 *  - The tab's own actor and own car are always in its view. Interiors keep full syncing (everyone sees everyone).
 */
export const INTEREST = {
  avatarRadiusMetres: 200,
  hysteresisMetres: 30,
  overviewRadiusMinMetres: 50,
  overviewRadiusMaxMetres: 400,
  /** How often the server recomputes every tab's view. */
  updateHz: 5,
  /** How often a tab may send its Overview focus (the server applies the latest one at `updateHz`). */
  focusSendHz: 4,
} as const;

/** Generous limits for a focus point before the server clamps it to the town's bounds (metres). */
const FOCUS_COORD_LIMIT = 10_000;
/**
 * `view.focus` (ADR-0043): the Overview camera's ground focus and the radius it shows, or `focus: null` when the tab
 * left Overview (Walk, inside a building). Untrusted: validated here, then clamped by the server; it only widens what
 * this tab is sent, never what anyone may do.
 */
export const ViewFocus = z
  .object({
    focus: z
      .object({
        x: finiteNumber(-FOCUS_COORD_LIMIT, FOCUS_COORD_LIMIT),
        z: finiteNumber(-FOCUS_COORD_LIMIT, FOCUS_COORD_LIMIT),
        radius: finiteNumber(0, FOCUS_COORD_LIMIT),
      })
      .strict()
      .nullable(),
  })
  .strict();
export type ViewFocus = z.infer<typeof ViewFocus>;

/**
 * Bits of `InputMove.buttons` (ADR-0018). Only `run` has a meaning: the server walks the actor at
 * RUN_SPEED_MULTIPLIER x the walking speed (packages/world movement.ts) while it is set; every other
 * bit is ignored. The client only sets the bit; the server decides the speed.
 */
export const INPUT_BUTTONS = { run: 1 } as const;

export const InputMove = z
  .object({
    seq: z.number().int().min(0).max(2_147_483_647),
    moveX: finiteNumber(-1, 1),
    moveZ: finiteNumber(-1, 1),
    facing: finiteNumber(-Math.PI * 4, Math.PI * 4),
    buttons: z.number().int().min(0).max(255),
  })
  .strict();
export type InputMove = z.infer<typeof InputMove>;

/** `ActorState.anim` (public presentation hint): idle, walking, running (ADR-0018). */
export const ACTOR_ANIM = { idle: 0, walk: 1, run: 2 } as const;
export type ActorAnim = (typeof ACTOR_ANIM)[keyof typeof ACTOR_ANIM];

/** `ActorState.posture` (public): standing, sitting on a seat, lying on a bed (ADR-0018). */
export const ACTOR_POSTURE = { standing: 0, sitting: 1, lying: 2 } as const;
export type ActorPosture = (typeof ACTOR_POSTURE)[keyof typeof ACTOR_POSTURE];

export const CHAT_MAX_CHARS = 280;
export const ChatSend = z
  .object({
    clientMessageId: z.string().min(1).max(64),
    text: z.string().min(1).max(CHAT_MAX_CHARS),
  })
  .strict();
export type ChatSend = z.infer<typeof ChatSend>;

/** `chat.typing` (ADR-0053): no fields. */
export const ChatTyping = z.object({}).strict();
export type ChatTyping = z.infer<typeof ChatTyping>;

/**
 * Door and seat interactions. `enter` / `exit` / `return_to_plaza` move between rooms (targetId =
 * property id for `enter`); `sit` / `lie` take the seat `targetId` (a seat id from the shared seat
 * lists in packages/world seats.ts, ADR-0018) and `stand` gets up again (targetId ignored). Seat
 * actions are answered with `seat.result`. On the street, `return_to_plaza` goes to the square of the
 * district the visitor stands in. Town v6 (ADR-0042): world v5's `cross_gate` is gone (one street room,
 * no gates); like any other unknown action it fails validation.
 */
export const Interaction = z
  .object({
    action: z.enum(["enter", "exit", "return_to_plaza", "sit", "lie", "stand"]),
    targetId: z.string().max(64).optional(),
    expectedVersion: z.number().int().min(0).optional(),
  })
  .strict();
export type Interaction = z.infer<typeof Interaction>;
export type SeatAction = Extract<Interaction["action"], "sit" | "lie" | "stand">;
export const SEAT_ACTIONS: readonly SeatAction[] = ["sit", "lie", "stand"];

export const PresenceHint = z.object({ visibilityState: z.enum(["visible", "hidden"]) }).strict();
export type PresenceHint = z.infer<typeof PresenceHint>;

// ---------- server -> client messages ----------
export const SERVER_MESSAGES = {
  welcome: "room.welcome",
  controlChanged: "session.control_changed",
  transitionReady: "room.transition_ready",
  transitionFailed: "room.transition_failed",
  chatMessage: "chat.message",
  chatRejected: "chat.rejected",
  chatRemoved: "chat.removed",
  moderationNotice: "moderation.notice",
  /** ADR-0014: a new private message for this visitor (ids only; the client reads it from the API). */
  dmNotice: "dm.notice",
  propertyChanged: "property.changed",
  walletChanged: "wallet.changed",
  notificationCreated: "notification.created",
  serviceDraining: "service.draining",
  serviceDegraded: "service.degraded",
  /** Reply to vehicle.action, to the requesting client only (VehicleResult). */
  vehicleResult: "vehicle.result",
  /** Attractions (ADR-0016): shared game state (VenueStateMessage), own state (VenuePrivateMessage), notices (VenueEventMessage). */
  venueState: "venue.state",
  venuePrivate: "venue.private",
  venueEvent: "venue.event",
  /** Reply to a `sit` / `lie` / `stand` interaction, to the requesting client only (SeatResult, ADR-0018). */
  seatResult: "seat.result",
  /** ADR-0043: a `world.switch` was refused (WorldSwitchRefused, to the requester only); the visitor stays. */
  worldSwitchRefused: "world.switch_refused",
} as const;

export const RoomWelcome = z.object({
  roomKind: z.enum(["street", "interior"]),
  roomKey: z.string(),
  worldVersion: z.number().int(),
  /**
   * Street (town v6, ADR-0042): the district the visitor appeared in (its square, or the district of the door they came
   * out of). Informational only: the room is the whole town, and the district a visitor is in follows their position
   * (`districtOfPoint`).
   */
  districtId: DistrictId.optional(),
  propertyId: PropertyId.optional(),
  presentationVersion: z.number().int().optional(),
  /**
   * ADR-0043: the world copy (1 = Robin): on the street the room's own copy; inside a building the copy the visitor came
   * from (leaving returns there while it has room). Absent inside when the ticket named none.
   */
  worldCopy: WorldCopyNumber.optional(),
  yourActorId: ActorId,
  /** Server tick rate so the client can size its prediction buffer. */
  simulationHz: z.number().int(),
  /** Seconds a guest must be present before their first chat message. */
  chatDwellSeconds: z.number().int().min(0),
  chatMinIntervalMs: z.number().int().min(0),
});
export type RoomWelcome = z.infer<typeof RoomWelcome>;

export const ControlChanged = z.object({ reason: z.enum(["another_tab", "reconnected_elsewhere"]) });
export type ControlChanged = z.infer<typeof ControlChanged>;

export const TransitionReady = z.object({
  roomName: z.enum(["street", "interior"]),
  ticket: z.string(),
  joinOptions: z.record(z.string(), z.string()),
  propertyId: PropertyId.optional(),
  /**
   * A move to the street (town v6, ADR-0042): the district the visitor will appear in (the district of the door they
   * leave by). Informational only: there is one street room for the whole town.
   */
  districtId: DistrictId.optional(),
});
export type TransitionReady = z.infer<typeof TransitionReady>;
export const TransitionFailed = z.object({ code: z.string(), message: z.string() });
export type TransitionFailed = z.infer<typeof TransitionFailed>;

/**
 * Answer to a seat interaction (ADR-0018), sent right after the next state patch so the room state
 * already shows the posture. Codes: SAT / LAY / STOOD (ok), TOO_FAR (walk closer), OCCUPIED (someone
 * is on it), UNKNOWN_SEAT (not in this room now, e.g. the furniture was moved), WRONG_POSTURE ("sit"
 * on a bed, "lie" on a chair), NOT_ON_FOOT (driving or playing), NOT_SEATED ("stand" while standing),
 * RATE_LIMITED, NOT_AVAILABLE (no seats here / leaving the room), CLOSED (ADR-0019: a yard seat of a home its
 * owner has closed; only the owner may sit there meanwhile).
 */
export const SEAT_RESULT_CODES = [
  "SAT",
  "LAY",
  "STOOD",
  "TOO_FAR",
  "OCCUPIED",
  "UNKNOWN_SEAT",
  "WRONG_POSTURE",
  "NOT_ON_FOOT",
  "NOT_SEATED",
  "RATE_LIMITED",
  "NOT_AVAILABLE",
  "CLOSED",
] as const;
export const SeatResultCode = z.enum(SEAT_RESULT_CODES);
export type SeatResultCode = z.infer<typeof SeatResultCode>;
export const SeatResult = z.object({
  action: z.enum(["sit", "lie", "stand"]),
  ok: z.boolean(),
  seatId: z.string().max(64).nullable(),
  code: SeatResultCode,
  message: z.string().max(200),
});
export type SeatResult = z.infer<typeof SeatResult>;

export const ChatMessage = z.object({
  id: Uuid,
  actorId: ActorId,
  label: z.string(),
  countryCode: z.string().nullable(),
  text: z.string().max(CHAT_MAX_CHARS),
  at: IsoUtc,
});
export type ChatMessage = z.infer<typeof ChatMessage>;
export const ChatRejected = z.object({
  clientMessageId: z.string(),
  code: z.enum(["RATE_LIMITED", "DWELL_REQUIRED", "MUTED", "INVALID", "RESTRICTED"]),
  message: z.string(),
  retryAfterMs: z.number().int().min(0).optional(),
});
export type ChatRejected = z.infer<typeof ChatRejected>;
export const ChatRemoved = z.object({ id: Uuid });
export const ModerationNotice = z.object({
  code: z.enum(["MUTED", "UNMUTED", "KICKED", "WARNING", "ROOM_SUSPENDED"]),
  message: z.string(),
  until: IsoUtc.nullable(),
});
export type ModerationNotice = z.infer<typeof ModerationNotice>;
export const PropertyChanged = z.object({
  propertyId: PropertyId,
  ownershipIntervalId: Uuid.nullable(),
  version: z.number().int(),
  presentationVersion: z.number().int().optional(),
  /**
   * ADR-0019 "Send visitors out": the owner asked everyone else inside to leave at this server time. The property's
   * room moves every non-owner who arrived before it to the street at the entrance (a repeated event is harmless).
   */
  visitorsOutAt: IsoUtc.optional(),
});
export type PropertyChanged = z.infer<typeof PropertyChanged>;
export const ServiceDraining = z.object({ message: z.string(), graceSeconds: z.number().int() });
export type ServiceDraining = z.infer<typeof ServiceDraining>;

/** Custom WebSocket close codes (4011-4999 are application codes in Colyseus 0.18). */
export const CLOSE_CODES = {
  CONTROL_TAKEN: 4011,
  KICKED: 4012,
  BANNED: 4013,
  TICKET_INVALID: 4014,
  ROOM_FULL: 4015,
  ROOM_SUSPENDED: 4016,
  INCOMPATIBLE_CLIENT: 4017,
} as const;

/**
 * Room protocol version (docs/11 section 4, ADR-0036). The browser sends it as the `protocolVersion` join option on
 * EVERY room join (`withProtocolVersion`); the realtime server refuses a join whose version is missing or outside
 * [REALTIME_MIN_CLIENT_PROTOCOL_VERSION, REALTIME_PROTOCOL_VERSION] with `CLIENT_OUTDATED`, and the client asks the
 * visitor to reload (`CLIENT_OUTDATED_MESSAGE`). Bump REALTIME_PROTOCOL_VERSION when a change to room messages, join
 * options or synchronized state would break a tab that is already open; raise the minimum together with it unless the
 * server keeps accepting the older tabs. Clients from before ADR-0036 send no version and are refused.
 * Version 2 (world v5, ADR-0041): three districts, district gates (`cross_gate`, gate arrivals) and three-digit
 * property ids; a v4 tab cannot follow them, so the minimum rises with it and open v4 tabs are asked to reload.
 * Version 3 (town v6, ADR-0042): one street room for the whole town (`street:6:town:1`), no gates and no `cross_gate`,
 * street tickets with `spawnDistrictId`; a v5 tab would ask for per-district rooms, so the minimum rises with it and
 * open v5 tabs reload once.
 * Version 4 (ADR-0043): nearby-only syncing on the street: the street state's `actors` / `vehicles` hold only what is
 * near the tab (people out of range leave the map without having left the room), the room's people count is the state
 * field `peopleCount`, and Overview sends `view.focus`. A v3 tab would count only the people near it and lose sight of
 * everyone in other districts, so the minimum rises with it and open v3 tabs reload once.
 * Still version 4 with named world copies (ADR-0043 slices 2-3): every change is additive for an open v4 tab. It passes
 * a ticket's join options verbatim (the new `copy`, `fromCopy` and `arrive*` included), ignores the welcome's
 * `worldCopy`, never sends `world.switch` (so never gets `world.switch_refused`), and its HUD count is the room's
 * `peopleCount`, which is now its copy's (the meaning the HUD wants). It only lacks the Worlds list until it reloads.
 */
export const REALTIME_PROTOCOL_VERSION = 4;
/** Oldest client protocol version the realtime server still accepts. */
export const REALTIME_MIN_CLIENT_PROTOCOL_VERSION = 4;
/** The join option that carries the client's protocol version (a decimal string, like the ticket's join options). */
export const PROTOCOL_VERSION_OPTION = "protocolVersion";
/** What a visitor with an outdated tab is told (also the detail of the `CLIENT_OUTDATED` refusal, see ADR-0036). */
export const CLIENT_OUTDATED_MESSAGE = "Townsy was updated. Reload the page to continue.";

/** The ticket's join options plus this client's protocol version (the ticket's own options are never changed). */
export function withProtocolVersion(joinOptions: Readonly<Record<string, string>>, version: number = REALTIME_PROTOCOL_VERSION): Record<string, string> {
  return { ...joinOptions, [PROTOCOL_VERSION_OPTION]: String(version) };
}

export type ClientProtocolRefusal = { ok: false; reason: "missing" | "invalid" | "too_old" | "too_new"; version: number | null };
export type ClientProtocolCheck = { ok: true; version: number } | ClientProtocolRefusal;

/**
 * Whether join options carry a protocol version this server accepts. Accepts the decimal string the browser sends and
 * a plain integer; anything else (absent, empty, fractional, negative, oversized, another type) is refused.
 */
export function checkClientProtocol(
  options: unknown,
  accepted: { min: number; max: number } = { min: REALTIME_MIN_CLIENT_PROTOCOL_VERSION, max: REALTIME_PROTOCOL_VERSION },
): ClientProtocolCheck {
  const raw = options !== null && typeof options === "object" ? (options as Record<string, unknown>)[PROTOCOL_VERSION_OPTION] : undefined;
  if (raw === undefined || raw === null || raw === "") return { ok: false, reason: "missing", version: null };
  const version = typeof raw === "number" ? raw : typeof raw === "string" && /^\d{1,6}$/.test(raw) ? Number(raw) : Number.NaN;
  if (!Number.isSafeInteger(version) || version < 0) return { ok: false, reason: "invalid", version: null };
  if (version < accepted.min) return { ok: false, reason: "too_old", version };
  if (version > accepted.max) return { ok: false, reason: "too_new", version };
  return { ok: true, version };
}
